/**
 * PrescriberField.tsx
 *
 * The medication form's Prescriber input. It offers the account's active
 * doctors and nurses (GET /v5/doctors and /v5/nurses) as a dropdown, but the
 * field stays free text: someone outside the practice can be typed in, and the
 * medication stores the name either way, never a staff id.
 *
 * The full list shows while the field is empty or holds one of the names;
 * typing anything else narrows it to the names that contain the text.
 *
 * Keyboard: Arrow keys move through the list, Enter picks, Escape closes.
 */
import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui';
import { formatDoctorName, listDoctors } from '@/services/doctors.service';
import { formatNurseName, listNurses } from '@/services/nurses.service';

interface PrescriberFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

export function PrescriberField({ value, onChange, error, disabled }: PrescriberFieldProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  // Same keys and calls as the other active-staff selects, so they share a cache.
  const { data: doctors = [] } = useQuery({
    queryKey: ['doctors', 'active'],
    queryFn: () => listDoctors({ active: true }),
    enabled: !disabled,
  });
  const { data: nurses = [] } = useQuery({
    queryKey: ['nurses', 'active'],
    queryFn: () => listNurses({ active: true }),
    enabled: !disabled,
  });

  // One person can hold a row in both tables; list each name once.
  const names = useMemo(() => {
    const all = [...doctors.map(formatDoctorName), ...nurses.map(formatNurseName)].filter((n) => n !== '');
    return Array.from(new Set(all)).sort((a, b) => a.localeCompare(b));
  }, [doctors, nurses]);

  const options = useMemo(() => {
    const typed = value.trim().toLowerCase();
    if (typed === '' || names.some((n) => n.toLowerCase() === typed)) return names;
    return names.filter((n) => n.toLowerCase().includes(typed));
  }, [names, value]);

  const showList = open && !disabled && options.length > 0;

  function pick(name: string) {
    onChange(name);
    setOpen(false);
    setHighlight(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? options.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      pick(options[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <div className="relative">
      <Input
        label="Prescriber"
        placeholder="Choose or type a name"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        error={error}
        disabled={disabled}
        maxLength={120}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && highlight >= 0 ? `${listId}-${highlight}` : undefined}
        rightAddon={<ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
        >
          {options.map((name, i) => (
            <li
              key={name}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === highlight}
              className={`cursor-pointer px-3 py-2 ${i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              // mousedown, not click: the input's blur would close the list first.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(name);
              }}
            >
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
